import React from 'react';
import { View, StyleSheet, useWindowDimensions } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import Svg, {
    Defs,
    LinearGradient,
    RadialGradient,
    Stop,
    Path,
    Rect,
    Circle,
    G,
} from 'react-native-svg';
import { studentTheme } from '../../theme/tokens';

interface AtmosphericStageProps {
    children: React.ReactNode;
    style?: object;
    testID?: string;
}

/**
 * AtmosphericStage — Full-bleed vector environment (sunburst sky, floating clouds, rolling green meadow hills).
 * Eliminates clinical test anxiety and provides the joyful world depicted in the design system reference.
 */
export const AtmosphericStage: React.FC<AtmosphericStageProps> = ({
    children,
    style,
    testID = 'atmospheric-stage',
}) => {
    const { width, height } = useWindowDimensions();

    return (
        <View testID={testID} style={[styles.root, style]}>
            {/* Vector Background Illustration */}
            <View style={StyleSheet.absoluteFill} pointerEvents="none">
                <Svg width={width} height={height} viewBox="0 0 1000 700" preserveAspectRatio="xMidYMid slice">
                    <Defs>
                        <LinearGradient id="skyGrad" x1="0" y1="0" x2="0" y2="1">
                            <Stop offset="0%" stopColor={studentTheme.colors.sky.top} />
                            <Stop offset="65%" stopColor={studentTheme.colors.sky.horizon} />
                            <Stop offset="100%" stopColor="#C2EFFF" />
                        </LinearGradient>
                        <LinearGradient id="hillBackGrad" x1="0" y1="0" x2="0" y2="1">
                            <Stop offset="0%" stopColor={studentTheme.colors.meadow.back} />
                            <Stop offset="100%" stopColor={studentTheme.colors.meadow.frontDark} />
                        </LinearGradient>
                        <LinearGradient id="hillFrontGrad" x1="0" y1="0" x2="0" y2="1">
                            <Stop offset="0%" stopColor={studentTheme.colors.meadow.front} />
                            <Stop offset="100%" stopColor={studentTheme.colors.meadow.trees} />
                        </LinearGradient>
                        <RadialGradient id="sunGlow" cx="500" cy="80" r="450" fx="500" fy="80" gradientUnits="userSpaceOnUse">
                            <Stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.6" />
                            <Stop offset="40%" stopColor="#8FE2FF" stopOpacity="0.25" />
                            <Stop offset="100%" stopColor="#4FB4F7" stopOpacity="0" />
                        </RadialGradient>
                    </Defs>

                    {/* Sky Base */}
                    <Rect x="0" y="0" width="1000" height="700" fill="url(#skyGrad)" />

                    {/* Sunburst Beams radiating from top center (500, 80) */}
                    <G opacity="0.35">
                        <Path d="M 500 80 L 100 0 L 180 0 Z" fill="#FFFFFF" />
                        <Path d="M 500 80 L 260 0 L 340 0 Z" fill="#FFFFFF" />
                        <Path d="M 500 80 L 420 0 L 500 0 Z" fill="#FFFFFF" />
                        <Path d="M 500 80 L 580 0 L 660 0 Z" fill="#FFFFFF" />
                        <Path d="M 500 80 L 740 0 L 820 0 Z" fill="#FFFFFF" />
                        <Path d="M 500 80 L 900 0 L 980 0 Z" fill="#FFFFFF" />
                        <Path d="M 500 80 L 1000 60 L 1000 140 Z" fill="#FFFFFF" />
                        <Path d="M 500 80 L 1000 220 L 1000 300 Z" fill="#FFFFFF" />
                        <Path d="M 500 80 L 0 60 L 0 140 Z" fill="#FFFFFF" />
                        <Path d="M 500 80 L 0 220 L 0 300 Z" fill="#FFFFFF" />
                    </G>

                    {/* Radial Sun Glow */}
                    <Rect x="0" y="0" width="1000" height="700" fill="url(#sunGlow)" />

                    {/* Stylized Puffy Clouds */}
                    {/* Cloud Top Left */}
                    <G fill="#FFFFFF" opacity="0.95">
                        <Circle cx="120" cy="110" r="35" />
                        <Circle cx="155" cy="95" r="42" />
                        <Circle cx="195" cy="110" r="32" />
                        <Rect x="120" y="110" width="80" height="32" rx="16" />
                    </G>

                    {/* Cloud Top Right */}
                    <G fill="#FFFFFF" opacity="0.95">
                        <Circle cx="810" cy="100" r="32" />
                        <Circle cx="850" cy="85" r="40" />
                        <Circle cx="890" cy="102" r="34" />
                        <Rect x="810" y="100" width="85" height="34" rx="17" />
                    </G>

                    {/* Background Rolling Hill */}
                    <Path
                        d="M -50 700 L -50 560 Q 200 500 500 540 Q 800 580 1050 520 L 1050 700 Z"
                        fill="url(#hillBackGrad)"
                    />

                    {/* Stylized Trees on Background Ridge */}
                    <G fill={studentTheme.colors.meadow.trees} opacity="0.9">
                        <Circle cx="180" cy="530" r="16" />
                        <Circle cx="205" cy="525" r="22" />
                        <Circle cx="230" cy="532" r="15" />
                        <Circle cx="780" cy="545" r="18" />
                        <Circle cx="810" cy="540" r="24" />
                        <Circle cx="840" cy="548" r="16" />
                    </G>

                    {/* Foreground Rolling Hill */}
                    <Path
                        d="M -50 700 L -50 610 Q 300 540 600 600 Q 850 650 1050 590 L 1050 700 Z"
                        fill="url(#hillFrontGrad)"
                    />
                </Svg>
            </View>

            {/* Foreground Content */}
            <SafeAreaView style={styles.safeArea}>
                <View style={styles.content}>
                    {children}
                </View>
            </SafeAreaView>
        </View>
    );
};

const styles = StyleSheet.create({
    root: {
        flex: 1,
        backgroundColor: studentTheme.colors.sky.top,
        overflow: 'hidden',
    },
    safeArea: {
        flex: 1,
        minHeight: 0,
    },
    content: {
        flex: 1,
        minHeight: 0,
    },
});
